import React, {useState, useEffect, useRef, useCallback} from "react";
import {useContextSelector, useContext} from "use-context-selector";
import {useLocation} from "react-router-dom";
import {Modal, Button, Card} from "react-bootstrap";
import {LinkContainer} from "react-router-bootstrap";
import {faFacebook} from "@fortawesome/free-brands-svg-icons/faFacebook";
import {faLinkedin} from "@fortawesome/free-brands-svg-icons/faLinkedin";
import {faInstagram} from "@fortawesome/free-brands-svg-icons/faInstagram";
import {faTwitter} from "@fortawesome/free-brands-svg-icons/faTwitter";
import {faBuilding} from "@fortawesome/free-solid-svg-icons/faBuilding";
import {faSyncAlt} from "@fortawesome/free-solid-svg-icons/faSyncAlt";

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Helmet} from "react-helmet";
import Layout from "../common/Layout";
import ProviderReviewsList from "./ProviderReviewsList";
import ProviderNPSCounter from "./ProviderNPSCounter";
import {useTranslation} from "react-i18next";
import useGetProvider from "./useGetProvider";
import useCreateClientStatusMessage from "../util/useCreateClientStatusMessage";
import useGetFields from "./useGetFields";
import {LocalContext} from "../context/LocalContext";
import {SessionContext} from "../context/SessionContext";
import createUUID from "../util/createUUID";
import NotFound from "../routed/NotFound";
import ProvidersCheckoutContact from "./ProvidersCheckoutContact";
import ModalRef from "../util/ModalRef";
import useWindowSize from "../util/useWindowSize";

import useGetProvidersSummaryPDFURL from "./useGetProvidersSummaryPDFURL";
import useGetProvidersSummaryPDFFile from "./useGetProvidersSummaryPDFFile";

const Provider = () => {
    const {t} = useTranslation();
    const location = useLocation();
    const width = useWindowSize()[1];

    const isMounted = useRef(null);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const [modeContactProvider, setModeContactProvider] = useState(false);

    const clientOnline = useContextSelector(LocalContext, (v) => v[0].clientOnline);

    const cookiesPolicyAccepted = useContextSelector(LocalContext, (v) => v[0].cookiesPolicy.accepted);
    const UILangGuest = useContextSelector(LocalContext, (v) => v[0].UILang);
    const UILangSession = useContextSelector(SessionContext, (v) => v[0].UILang);

    const [UILang, setUILang] = useState(null);

    const [sessionData, setSessionData] = useContext(SessionContext);
    const modalVisible = useContextSelector(SessionContext, (v) => v[0].modalVisible);

    useEffect(() => {
        const currentUILang = cookiesPolicyAccepted ? UILangGuest : UILangSession;
        if (UILang !== currentUILang) {
            setUILang(currentUILang);
        }
    }, [cookiesPolicyAccepted, UILangGuest, UILangSession, setUILang, UILang]);

    const getAPIDataProvider = useGetProvider();
    const getAPIDataFields = useGetFields();
    const createClientStatusMessage = useCreateClientStatusMessage();

    const getProvidersSummaryPDFURL = useGetProvidersSummaryPDFURL();
    const getProvidersSummaryPDFFile = useGetProvidersSummaryPDFFile();

    const [isLoadingGetProvidersSummaryPDF, setIsLoadingGetProvidersSummaryPDF] = useState(false);

    const [APIDataProvider, setAPIDataProvider] = useState(null);
    const [isLoadingAPIDataProvider, setIsLoadingAPIDataProvider] = useState(false);
    const [isLoadedAPIDataProvider, setIsLoadedAPIDataProvider] = useState(false);
    const [isErrorGetAPIDataProvider, setIsErrorGetAPIDataProvider] = useState(false);
    const [isNotFoundAPIDataProvider, setIsNotFoundAPIDataProvider] = useState(false);

    const handleGetAPIDataProvider = useCallback(async () => {
        setIsLoadingAPIDataProvider(true);

        let response;
        try {
            response = await getAPIDataProvider(location.pathname.split("/").slice(-1)[0]);
        } catch (error) {
            if (error.status === 404) {
                if (isMounted.current) {
                    setIsNotFoundAPIDataProvider(true);
                    setIsLoadingAPIDataProvider(false);
                    return;
                }
            } else {
                if (isMounted.current) {
                    setIsErrorGetAPIDataProvider(true);
                    createClientStatusMessage(error.createClientStatusMessage, true);
                    setIsLoadingAPIDataProvider(false);
                }
            }
            return;
        }

        if (isMounted.current) {
            // console.log(response)
            setAPIDataProvider(response);
            setIsLoadedAPIDataProvider(true);
            setIsLoadingAPIDataProvider(false);
        }
    }, [setIsLoadingAPIDataProvider, getAPIDataProvider, location, setIsLoadedAPIDataProvider, setIsNotFoundAPIDataProvider, setIsErrorGetAPIDataProvider, createClientStatusMessage, setAPIDataProvider]);


    useEffect(() => {
        if (clientOnline && UILang !== null && !isErrorGetAPIDataProvider && !isNotFoundAPIDataProvider && !isLoadingAPIDataProvider && !isLoadedAPIDataProvider) {
            handleGetAPIDataProvider();
        }
    }, [clientOnline, UILang, isErrorGetAPIDataProvider, isNotFoundAPIDataProvider, isLoadingAPIDataProvider, isLoadedAPIDataProvider, setIsLoadingAPIDataProvider, handleGetAPIDataProvider]);

    const [APIDataFields, setAPIDataFields] = useState({});
    const [langAPIDataFields, setLangAPIDataFields] = useState(null);
    const [isErrorGetAPIDataFields, setIsErrorGetAPIDataFields] = useState(false);
    const [isLoadingAPIDataFields, setIsLoadingAPIDataFields] = useState(false);
    const [isLoadedAPIDataFields, setIsLoadedAPIDataFields] = useState(false);

    const handleGetAPIDataFields = useCallback(async () => {
        setIsLoadingAPIDataFields(true);
        setLangAPIDataFields(UILang);

        let responseAPIDataFields;

        try {
            responseAPIDataFields = await getAPIDataFields({lang: UILang, returnActive: false});
        } catch (error) {
            if (isMounted.current) {
                createClientStatusMessage(error.clientStatusMessage);
                setIsErrorGetAPIDataFields(true);
                setIsLoadingAPIDataFields(false);
                return;
            }
        }

        if (isMounted.current) {
            setAPIDataFields(responseAPIDataFields);

            setIsLoadedAPIDataFields(true);

            // console.log(responseAPIDataFields);

            setIsLoadingAPIDataFields(false);
        }
    }, [UILang, setIsErrorGetAPIDataFields, setAPIDataFields, setIsLoadedAPIDataFields, setIsLoadingAPIDataFields, getAPIDataFields, createClientStatusMessage]);

    useEffect(() => {
        if (clientOnline && UILang !== null && !isNotFoundAPIDataProvider && !isErrorGetAPIDataFields && !isLoadingAPIDataFields && (!isLoadedAPIDataFields || langAPIDataFields !== UILang)) {
            handleGetAPIDataFields();
        }
    }, [clientOnline, UILang, langAPIDataFields, isErrorGetAPIDataFields, isLoadingAPIDataFields, isLoadedAPIDataFields, isNotFoundAPIDataProvider, setIsLoadingAPIDataFields, handleGetAPIDataFields]);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadingData = isLoadingAPIDataFields || isLoadingAPIDataProvider || isLoadingGetProvidersSummaryPDF;

        if (!loading && loadingData) {
            setLoading(true);
        } else if (loading && !loadingData) {
            setLoading(false);
        }
    }, [isLoadingAPIDataFields, isLoadingAPIDataProvider, loading, setLoading, isLoadingGetProvidersSummaryPDF]);

    const [providerContacted, setProviderContacted] = useState(false);
    const [providerContactedMessageSeen, setProviderContactedMessageSeen] = useState(false);
    const [providerContactedMessage, setProviderContactedMessage] = useState(false);
    const [providerContactedError, setProviderContactedError] = useState(false);

    // const [isLoadedGetProvidersSummaryPDF, setIsLoadedGetProvidersSummaryPDF] = useState(false);
    // const [isErrorGetProvidersSummaryPDF, setIsErrorGetProvidersSummaryPDF] = useState(false);
    const setIsLoadedGetProvidersSummaryPDF = useState(false)[1];
    const setIsErrorGetProvidersSummaryPDF = useState(false)[1];

    const handleGetProvidersSummaryPDF = useCallback(async () => {
        setIsLoadingGetProvidersSummaryPDF(true);

        let response;
        try {
            response = await getProvidersSummaryPDFURL([{id: APIDataProvider.id}]);
        } catch (error) {
            if (isMounted.current) {
                setIsErrorGetProvidersSummaryPDF(true);
                createClientStatusMessage(error.clientStatusMessage);
                setIsLoadingGetProvidersSummaryPDF(false);
                return;
            }
        }

        let fileBlob;
        try {
            fileBlob = await getProvidersSummaryPDFFile(response.pdf);
        } catch (error) {
            setIsErrorGetProvidersSummaryPDF(true);
            createClientStatusMessage(error.clientStatusMessage);
            setIsLoadingGetProvidersSummaryPDF(false);
            return;
        }

        if (isMounted.current) {
            const fileURLObj = window.URL.createObjectURL(fileBlob);
            const fileAnchorElem = document.createElement("a");
            fileAnchorElem.href = fileURLObj;
            fileAnchorElem.download = `providers-summary-${Math.round(+new Date() / 1000)}-innocape.pdf`;
            fileAnchorElem.click();
            setIsLoadingGetProvidersSummaryPDF(false);
            setIsLoadedGetProvidersSummaryPDF(true);
        }
    }, [setIsLoadedGetProvidersSummaryPDF, setIsLoadingGetProvidersSummaryPDF, setIsErrorGetProvidersSummaryPDF, createClientStatusMessage, getProvidersSummaryPDFFile, isMounted, getProvidersSummaryPDFURL, APIDataProvider]);

    return (
        <>
            {modeContactProvider &&
                <ProvidersCheckoutContact
                    providerEntrySingle={true}
                    providersSelected={[APIDataProvider]}
                    setModeContactProviders={setModeContactProvider}
                    setModeProvidersCheckout={null}
                    setProvidersContacted={setProviderContacted}
                    setProvidersContactedMessage={setProviderContactedMessage}
                    setProvidersContactedError={setProviderContactedError}
                    filters={[]}
                    spawnedFrom="view"
                />
            }

            {(providerContacted || providerContactedError) && !providerContactedMessageSeen && (
                <Modal
                    container={ModalRef}
                    show={true}
                    onHide={() => {
                        if (modalVisible) {
                            setSessionData({
                                ...sessionData,
                                modalVisible: false,
                            });
                        }
                    }}
                    onShow={() => {
                        if (!modalVisible) {
                            setSessionData({
                                ...sessionData,
                                modalVisible: true,
                            });
                        }
                    }}
                    centered={true}
                >
                    <Modal.Body>{providerContactedMessage}</Modal.Body>
                    <Modal.Footer>
                        <Button onClick={() => setProviderContactedMessageSeen(true)}>{t("Provider.ProviderContact.close")}</Button>
                    </Modal.Footer>
                </Modal>
            )}

            <Layout navSecondaryShow={true} className="bg-light" contentBackgroundArt={true}>
                {isLoadedAPIDataProvider && isLoadedAPIDataFields && (
                    <>
                        <Helmet>
                            <title>{`${t("Provider.provider")}: ${APIDataProvider.title}`}</title>
                        </Helmet>
                        <div
                            style={{
                                display: "flex",
                                maxWidth: "120rem",
                                width: "100%",
                                margin: "0 auto",
                                flexWrap: "wrap",
                                flexShrink: 0,
                                padding: "1rem",
                                paddingTop: "0rem",
                                position: "relative",
                                zIndex: 1,
                            }}
                        >
                            <div
                                style={{
                                    maxWidth: "100vw",
                                    flexGrow: 1,
                                    flexShrink: 1,
                                    flexBasis: "25rem",
                                    padding: "1rem",
                                    marginTop: "-1rem",
                                    marginBottom: "-2rem",
                                }}
                            >
                                <Card
                                    style={{
                                        overflow: "hidden",
                                        width: "100%",
                                        border: "3px solid #ececec",
                                        // boxShadow: '.100rem .125rem 0.25rem rgba(0,0,0,0.045)',
                                        borderTop: "none",
                                        borderRadius: "1.2rem",
                                        borderTopLeftRadius: 0,
                                        borderTopRightRadius: 0,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            position: "relative",
                                        }}
                                    >
                                        <div
                                            style={{
                                                flexGrow: 1,
                                                flexShrink: 1,
                                                position: "absolute",
                                                left: 0,
                                                top: 0,
                                                right: 0,
                                                height: "12rem",
                                                background: "linear-gradient(180deg, rgba(0,0,90,0.035) 0%, rgba(0,0,70,0) 100%)",
                                            }}
                                        ></div>
                                        <div
                                            style={{
                                                flexGrow: 2,
                                                flexShrink: 1,
                                                display: "flex",
                                                position: "relative",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    flexShrink: 2,
                                                    flexGrow: 0,
                                                    padding: width > 600 ? "3rem 4rem" : '2rem 2rem',
                                                    marginBottom: "-2rem",
                                                    paddingRight: 0,
                                                    paddingBottom: 0,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        minWidth: "14rem",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            height: 0,
                                                            paddingTop: "100%",
                                                            marginBottom: 0,
                                                            position: "relative",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                position: "absolute",
                                                                paddingBottom: 0,
                                                                paddingTop: 0,
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                background: "#ddd",
                                                                borderRadius: "1rem",
                                                                overflow: "hidden",
                                                                boxShadow: "0px 0px 15px 0px rgba(0,0,0,0.075)",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                color: "#bbb",
                                                            }}
                                                        >
                                                            {APIDataProvider.logo ? (
                                                                <img
                                                                    style={{
                                                                        width: "100%",
                                                                        height: "100%",
                                                                        objectFit: "cover",
                                                                    }}
                                                                    alt={`Branding for ${APIDataProvider.title}`}
                                                                    src={`${process.env.REACT_APP_HOST}${APIDataProvider.logo}`}
                                                                />
                                                            ) : (
                                                                    <span
                                                                        style={{
                                                                            fontSize: "2rem",
                                                                            display: "inline-flex",
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faBuilding} />
                                                                    </span>
                                                                )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    flexGrow: 2,
                                                    flexShrink: 1,
                                                    flexBasis: "30rem",
                                                    padding: width > 600 ? "3.5rem 4rem" : "3.5rem 2rem",
                                                }}
                                            >
                                                <style>
                                                    {`
                                                    .provider-entry-contact-info__details-snippet__item:not(:last-of-type) {
                                                        margin-right: 0.5em;
                                                    }

                                                    .provider-entry-contact-info__details-snippet__item:not(:first-of-type):before {
                                                        content: '/';
                                                        color: lightgray;
                                                        padding-right: 0.5em;

                                                    }
                                                `}
                                                </style>
                                                <div>
                                                    {APIDataProvider.title &&
                                                        <h1 className="mb-0" style={{fontWeight: 500, fontSize: "2.3rem"}}>
                                                            {APIDataProvider.title}
                                                        </h1>
                                                    }

                                                    <p
                                                        className="text-muted mt-1 mb-0"
                                                        style={{
                                                            fontSize: "0.75rem",
                                                        }}
                                                    >
                                                        {APIDataProvider.country &&
                                                            <span className="provider-entry-contact-info__details-snippet__item">
                                                                <span>{t(`country.${APIDataProvider.country.name}`)}</span>
                                                            </span>
                                                        }
                                                        {APIDataProvider.industry &&
                                                        <span className="provider-entry-contact-info__details-snippet__item">
                                                            <span>{
                                                                APIDataFields.industry.find((value) => value.id === APIDataProvider.industry.id)?.name
                                                            }</span>
                                                        </span>}
                                                    </p>
                                                </div>

                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: "100%",
                                                            marginTop: "1.75rem",
                                                            display: "inline",
                                                            flexGrow: 1,
                                                            flexShrink: 1,
                                                            flexWrap: "wrap",
                                                        }}
                                                    >
                                                        <Button
                                                            variant="dark"
                                                            className="mr-2 px-4 mb-2"
                                                            disabled={!clientOnline || isLoadingGetProvidersSummaryPDF}
                                                            style={{borderRadius: "5rem", flexShrink: 0}}
                                                            onClick={async (event) => {
                                                                event.preventDefault();
                                                                await handleGetProvidersSummaryPDF();
                                                            }}
                                                        >
                                                            <span>
                                                                {isLoadingGetProvidersSummaryPDF ? (
                                                                    <span className="mr-2">
                                                                        <FontAwesomeIcon spin={isLoadingGetProvidersSummaryPDF} size="sm" icon={faSyncAlt} />
                                                                    </span>
                                                                ) : null}
                                                                <span>{t("Provider.get-summary-pdf")}</span>
                                                            </span>
                                                        </Button>
                                                        <Button
                                                            style={{borderRadius: "5rem", flexShrink: 0}}
                                                            className="px-4 mb-2"
                                                            disabled={loading || modeContactProvider || providerContacted}
                                                            onClick={() => {
                                                                setModeContactProvider(true);
                                                            }}
                                                        >
                                                            {t("Provider.send-message")}
                                                        </Button>
                                                    </div>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            marginTop: "1.75rem",
                                                            flexGrow: 1,
                                                            flexShrink: 1,
                                                            flexBasis: "15rem",
                                                        }}
                                                    >
                                                        <style>
                                                            {`
                                                                .provider-entry-contact-info__social-link {
                                                                    border: 3px solid #ececec;
                                                                    color: var(--primary);
                                                                    width: 2.95rem;
                                                                    height: 2.95rem;
                                                                    display: flex;
                                                                    align-items: center;
                                                                    font-size: 1.25rem;
                                                                    justify-content: center;
                                                                    border-radius: 5rem;
                                                                    margin-right: 0.75rem;
                                                                    margin-bottom: 0.5rem;

                                                                }
                                                            `}
                                                        </style>
                                                        {APIDataProvider.social_profiles.map((value) => {
                                                            let icon;
                                                            let title = t(`Provider.social-profile-${value.network}`);
                                                            switch (value.network) {
                                                                case "facebook":
                                                                    icon = faFacebook;
                                                                    break;
                                                                case "linkedin":
                                                                    icon = faLinkedin;
                                                                    break;
                                                                case "twitter":
                                                                    icon = faTwitter;
                                                                    break;
                                                                case "instagram":
                                                                    icon = faInstagram;
                                                                    break;
                                                                default:
                                                                    break;
                                                            }
                                                            return (
                                                                <a href={value.url} title={title} key={createUUID()} target="_blank" rel="noopener noreferrer" className="provider-entry-contact-info__social-link">
                                                                    <span>
                                                                        <FontAwesomeIcon icon={icon} />
                                                                    </span>
                                                                </a>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                <div
                                                    style={{
                                                        marginTop: "1.5rem",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: "grid",
                                                            rowGap: "1.25rem",
                                                            columnGap: "2rem",
                                                            gridTemplateColumns: `repeat(auto-fit, minmax(15rem, 1fr))`,
                                                            width: "100%",
                                                            flexGrow: 1,
                                                            gridAutoFlow: "dense",
                                                            marginTop: "1.25rem",
                                                        }}
                                                    >
                                                        {APIDataProvider.email && (
                                                            <div>
                                                                <div
                                                                    style={{
                                                                        fontSize: "0.8rem",
                                                                    }}
                                                                >
                                                                    <strong>{t("Provider.contact-email")}</strong>
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        fontFamily: "Ubuntu",
                                                                    }}
                                                                >
                                                                    <span>{APIDataProvider.email}</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {APIDataProvider.phone && (
                                                            <div>
                                                                <div
                                                                    style={{
                                                                        fontSize: "0.8rem",
                                                                    }}
                                                                >
                                                                    <strong>{t("Provider.contact-phone")}</strong>
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        fontFamily: "Ubuntu",
                                                                    }}
                                                                >
                                                                    <span>{APIDataProvider.phone}</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {APIDataProvider.website && (
                                                            <div>
                                                                <div
                                                                    style={{
                                                                        fontSize: "0.8rem",
                                                                    }}
                                                                >
                                                                    <strong>{t("Provider.website")}</strong>
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        fontFamily: "Ubuntu",
                                                                    }}
                                                                >
                                                                    <span className="text-muted">
                                                                        <span>
                                                                            <a href={APIDataProvider.website} rel="noopener noreferrer" title={`Open ${APIDataProvider.title} Website in a new Tab`} target="_blank">
                                                                                {APIDataProvider.website}
                                                                            </a>
                                                                        </span>
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                            <div
                                style={{
                                    maxWidth: "100vw",
                                    flexGrow: 1,
                                    flexShrink: 1,
                                    flexBasis: "66rem",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <div
                                    style={{
                                        padding: "1rem",
                                        paddingTop: "2.5rem",
                                        display: "flex",
                                    }}
                                >
                                    <Card
                                        style={{
                                            flexGrow: 1,
                                            borderRadius: "1.2rem",
                                            border: "3px solid #ececec",
                                            // boxShadow: '.100rem .125rem 0.25rem rgba(0,0,0,0.045)',
                                            overflow: "hidden",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    flexWrap: "wrap",
                                                    flexGrow: 1,
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        padding: width > 1077 ? "4rem 4rem 0rem 4rem" : "1rem 2rem 2rem 2rem",
                                                        display: "flex",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: "block",
                                                            height: "1px",
                                                            flexGrow: 1,
                                                            marginLeft: "3rem",
                                                        }}
                                                    ></div>
                                                </div>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            padding: width > 600 ? "0rem 4rem 4rem 4rem" : '2rem',
                                                            flexGrow: 1,
                                                            flexShrink: 1,
                                                            flexBasis: "40rem",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                maxWidth: "32rem",
                                                            }}
                                                        >
                                                            {APIDataProvider.short_description && (
                                                                <p
                                                                    style={{
                                                                        fontSize: "1.25rem",
                                                                        fontFamily: "Ubuntu",
                                                                    }}
                                                                >
                                                                    {APIDataProvider.short_description}
                                                                </p>
                                                            )}
                                                            {APIDataProvider.full_description && (
                                                                <p className="mb-0" style={{lineHeight: 1.6, marginTop: "1.5rem"}}>
                                                                    {APIDataProvider.full_description}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div
                                                        style={{
                                                            padding: width > 600 ? "0rem 4rem 4rem 0" : '1rem 2rem 2rem 0',
                                                            flexBasis: "20rem",
                                                            flexGrow: 1,
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: "grid",
                                                                gridGap: "1rem",
                                                                rowGap: "1.5rem",
                                                                gridTemplateColumns: `repeat(auto-fit, minmax(15rem, 1fr))`,
                                                                paddingLeft: width > 600 ? "4rem" : '2rem',
                                                                lineHeight: 1.8,
                                                            }}
                                                        >
                                                            {APIDataProvider.industry && (
                                                                <div key={createUUID()}>
                                                                    <div
                                                                        style={{
                                                                            fontSize: "0.85rem",
                                                                        }}
                                                                    >
                                                                        <strong>{t("Provider.industry")}</strong>
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            fontFamily: "Ubuntu",
                                                                        }}
                                                                    >
                                                                        {APIDataFields.industry.find((value) => value.id === APIDataProvider.industry.id)?.name}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {APIDataProvider.country && (
                                                                <div key={createUUID()}>
                                                                    <div
                                                                        style={{
                                                                            fontSize: "0.85rem",
                                                                        }}
                                                                    >
                                                                        <strong>{t("Provider.country-of-origin")}</strong>
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            fontFamily: "Ubuntu",
                                                                        }}
                                                                    >
                                                                        {t(`country.${APIDataProvider.country.name}`)}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {APIDataFields.category.map((category) => {
                                                                const providerEntitiesInCategory = APIDataProvider.entity
                                                                    .filter((providerEntity) => {
                                                                        return providerEntity.category.indexOf(category.id) !== -1;
                                                                    })
                                                                    .map((value) => value.id);

                                                                const categoryEntitiesListed = category.entity_list.filter((entity) => {
                                                                    return providerEntitiesInCategory.indexOf(entity.id) !== -1;
                                                                });

                                                                const categoryEntitiesListedFragment = categoryEntitiesListed.map((entity, index) => {
                                                                    if (index !== categoryEntitiesListed.length - 1) {
                                                                        return <span key={createUUID()}>{entity.name}, </span>;
                                                                    } else {
                                                                        return <span key={createUUID()}>{entity.name}</span>;
                                                                    }
                                                                });
                                                                if (categoryEntitiesListed.length > 0) {
                                                                    return (
                                                                        <div key={createUUID()}>
                                                                            <div
                                                                                style={{
                                                                                    fontSize: "0.85rem",
                                                                                }}
                                                                            >
                                                                                <strong>{category.name}</strong>
                                                                            </div>
                                                                            <div
                                                                                style={{
                                                                                    fontFamily: "Ubuntu",
                                                                                }}
                                                                            >
                                                                                {categoryEntitiesListedFragment}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                } else {
                                                                    return null;
                                                                }
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                display: "flex",
                                                borderTop: "1px solid #dedede",
                                                flexWrap: "wrap",
                                                // flexDirection: 'column'
                                            }}
                                        >
                                            <div
                                                style={{
                                                    flexGrow: 1,
                                                    flexShrink: 1,
                                                    flexBasis: "40rem",
                                                    padding: width > 600 ? "4rem" : "2rem",
                                                }}
                                            >
                                                <ProviderReviewsList APIDataProvider={APIDataProvider} />
                                            </div>
                                            <style>
                                                {`
                                                    .provider-entry__info {
                                                        border-left: 1px solid #dedede;
                                                    }

                                                    @media(max-width: 1029px) {
                                                        .provider-entry__info {
                                                            border-left: none;
                                                            border-top: 1px solid #dedede;
                                                        }
                                                    }
                                                `}
                                            </style>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    padding: width > 600 ? "4rem" : "2rem",
                                                    flexBasis: "20rem",
                                                    flexGrow: 1,
                                                }}
                                                className="provider-entry__info"
                                            >
                                                <ProviderNPSCounter APIDataProvider={APIDataProvider} />
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "1rem",
                                        flexWrap: 'wrap'
                                    }}
                                >
                                    <div
                                        style={
                                            {
                                                margin: '1rem 0'
                                            }
                                        }
                                    >
                                        <p className="text-muted mb-0 mr-3">{t("Provider.explanation-more-providers")}</p>
                                    </div>
                                    <LinkContainer active={false} to="/providers-search">
                                        <Button size="sm" variant="primary" active={false}
                                            style={
                                                {
                                                    margin: '1rem 0'
                                                }
                                            }
                                        >
                                            {t("Provider.go-to-providers-search")}
                                        </Button>
                                    </LinkContainer>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                {isNotFoundAPIDataProvider && <NotFound />}
            </Layout>
        </>
    );
};

export default Provider;
